import type {
  BuildInfo,
  Company,
  CompanyId,
  CreateVelocityModel,
  DomainConvertInfo,
  DomainConvertObject,
  Field,
  FieldId,
  KrigingParams,
  KsuCompany,
  KsuCompanyId,
  KsuField,
  KsuFieldId,
  KsuLicenseArea,
  KsuLicenseAreaId,
  KsuWell,
  KsuWellsSearchGroup,
  LicenseAreaId,
  MapFetchingResult,
  MapId,
  MapInfo,
  Marker,
  MarkerCreationStats,
  MarkerToCreate,
  MinimalCurvatureParams,
  MovingAverageParams,
  ProjectVsp,
  ProjectVspId,
  RbfKernel,
  ResultCreateVelocityModel,
  SequentialConvergenceParams,
  VelocityModel,
  VspData,
  WellBrief,
  WellLinkedDataType,
  WellListItem,
} from '@core/core/types'
import type { MarkerId, Nullable, ProjectId, WellId } from 'common/types'

import { constructClientDispenser } from '../../clientDispenserFactory'
import { axiosInstance } from '../../common'
import { convertToApiElementType } from '../../converters'
import { CompaniesClient } from '../__generated__/Companies'
import { FieldsClient } from '../__generated__/Fields'
import { GisClient } from '../__generated__/Gis'
import { GridBuilderClient } from '../__generated__/GridBuilder'
import { GridFittingClient } from '../__generated__/GridFitting'
import { HttpClient } from '../__generated__/http-client'
import { InclinometryClient } from '../__generated__/Inclinometry'
import { KsuWellsClient } from '../__generated__/KsuWells'
import { LicenseAreasClient } from '../__generated__/LicenseAreas'
import { MarkersClient } from '../__generated__/Markers'
import { PointsetBuilderClient } from '../__generated__/PointsetBuilder'
import { ProjectFieldsClient } from '../__generated__/ProjectFields'
import { ProjectGisClient } from '../__generated__/ProjectGis'
import { ProjectGridClient } from '../__generated__/ProjectGrid'
import { ProjectInclinometryClient } from '../__generated__/ProjectInclinometry'
import { ProjectMarkersClient } from '../__generated__/ProjectMarkers'
import { ProjectPointsetClient } from '../__generated__/ProjectPointset'
import { ProjectPolygonClient } from '../__generated__/ProjectPolygon'
import { ProjectRigisClient } from '../__generated__/ProjectRigis'
import { ProjectVelocityModelClient } from '../__generated__/ProjectVelocityModel'
import { ProjectVspClient } from '../__generated__/ProjectVsp'
import { ProjectWellsClient } from '../__generated__/ProjectWells'
import { RigisClient } from '../__generated__/Rigis'
import { VelocityModelingClient } from '../__generated__/VelocityModeling'
import { VspClient } from '../__generated__/Vsp'
import { WellsClient } from '../__generated__/Wells'
import {
  convertToCompany,
  convertToElementConvertInfo,
  convertToField,
  convertToGridFittingParams,
  convertToMapFittingResult,
  convertToMapInfo,
  convertToMarker,
  convertToResultCreateVelocityModel,
  convertToVelocityModelHeader,
  convertToWellListItem,
  getCreationStats,
} from './converters'

const Clients = {
  CompaniesClient,
  FieldsClient,
  GisClient,
  RigisClient,
  GridBuilderClient,
  GridFittingClient,
  KsuWellsClient,
  MarkersClient,
  PointsetBuilderClient,
  ProjectPointsetClient,
  ProjectFieldsClient,
  ProjectGridClient,
  ProjectMarkersClient,
  InclinometryClient,
  ProjectInclinometryClient,
  ProjectVelocityModelClient,
  ProjectVspClient,
  ProjectGisClient,
  ProjectRigisClient,
  ProjectWellsClient,
  VelocityModelingClient,
  VspClient,
  WellsClient,
  LicenseAreasClient,
  ProjectPolygonClient,
} as const

const getClient = constructClientDispenser(Clients, HttpClient, axiosInstance)

/// companies

export const getAllCompaniesApi = (): Promise<Company[]> => {
  const client = getClient('CompaniesClient')
  return client.companiesGetAllCompaniesData().then(companies => companies.map(convertToCompany))
}

/// fields

export const getAllFieldsApi = (): Promise<Field[]> => {
  const client = getClient('FieldsClient')
  return client.fieldsGetAllFieldData().then(fields => fields.map(convertToField))
}

export const getFieldIdsForCompanyApi = (companyId: CompanyId): Promise<FieldId[]> => {
  const client = getClient('FieldsClient')
  return client.fieldsGetFieldIdsForCompany(companyId)
}

export const getFieldsHavingWellDataApi = (
  dataType: WellLinkedDataType = 'AnyWellData',
): Promise<Field[]> => {
  const client = getClient('FieldsClient')
  return client
    .fieldsGetFieldsHavingWellData(dataType)
    .then(fields => fields.map(({ id, name }) => ({ id, name })))
}

export const getFieldByProjectIdApi = (projectId: ProjectId): Promise<Field[]> => {
  const client = getClient('ProjectFieldsClient')
  return client
    .projectFieldsGetFieldsInProject(projectId)
    .then(fields => fields.map(convertToField))
}

/// wells

export const getWellsHavingDataByFieldIdApi = (
  fieldId: FieldId,
  dataType: WellLinkedDataType = 'AnyWellData',
): Promise<WellBrief[]> => {
  const client = getClient('WellsClient')
  return client
    .wellsGetWellsHavingDataForField(fieldId, dataType)
    .then(wells => wells.map(({ id, name }) => ({ id, name })))
}

export const getProjectWellAndFieldNamesApi = (projectId: ProjectId): Promise<WellListItem[]> => {
  const client = getClient('ProjectWellsClient')
  return client
    .projectWellsGetWellAndFieldNames(projectId)
    .then(names => names.map(convertToWellListItem))
    .catch(() => [])
}

/// project

export const addProjectWellsApi = (projectId: ProjectId, data: WellId[]): Promise<void> => {
  const client = getClient('ProjectWellsClient')
  return client.projectWellsAddWellsToProject(projectId, data)
}

/// Vsp

export const updateOffsetByProjectVspApi = (
  projectVspId: ProjectVspId,
  projectId: ProjectId,
  query: {
    offset: number
  },
): Promise<void> => {
  const client = getClient('ProjectVspClient')
  return client.projectVspUpdateOffsetByProjectVsp(projectVspId, projectId, query)
}

export const setActiveProjectVspApi = (
  projectId: ProjectId,
  projectWellVspId: WellId,
  vspId: ProjectVsp['vspId'],
): Promise<void> => {
  const client = getClient('ProjectVspClient')
  return client.projectVspSetActiveVsp(projectId, projectWellVspId, vspId)
}

export const applyVspProjectInWellsApi = (projectId: ProjectId, data: VspData): Promise<string> => {
  const client = getClient('ProjectVspClient')
  return client.projectVspAttachVsp(projectId, data)
}

/// markers

export const getMarkerNamesByFieldIdApi = (fieldId: FieldId): Promise<string[]> => {
  const client = getClient('MarkersClient')
  return client.markersGetMarkerNamesForField(fieldId)
}

export const getMarkersByFieldIdAndNameApi = (
  fieldId: FieldId,
  markerName: string,
): Promise<Marker[]> => {
  const client = getClient('MarkersClient')
  return client
    .markersGetMarkersForFieldByMarkerName(fieldId, markerName)
    .then(markers => markers.map(convertToMarker))
}

/// project markers

export const addFieldMarkersToProjectApi = (
  projectId: ProjectId,
  fieldIds: FieldId[],
): Promise<MarkerId[]> => {
  const client = getClient('ProjectMarkersClient')
  return client.projectMarkersAddFieldMarkersToProject(projectId, fieldIds)
}

export const createProjectMarkersApi = (
  projectId: ProjectId,
  markers: MarkerToCreate[],
  updateIfExists: boolean,
): Promise<MarkerCreationStats> => {
  const client = getClient('ProjectMarkersClient')
  return client
    .projectMarkersCreateProjectMarkers(projectId, { updateIfExists, markers })
    .then(getCreationStats)
}

export const addProjectMarkersApi = (projectId: ProjectId, data: MarkerId[]): Promise<string[]> => {
  const client = getClient('ProjectMarkersClient')
  return client.projectMarkersAddMarkersToProject(projectId, data)
}

/// ksu

export const getKsuAllCompaniesApi = (): Promise<KsuCompany[]> => {
  const client = getClient('KsuWellsClient')
  return client.ksuWellsGetAllCompanies()
}

export const getWellContainersByCompanyIdApi = async (
  companyKsuUid: KsuCompanyId,
): Promise<{ fields: KsuField[]; licenseAreas: KsuLicenseArea[] }> => {
  const client = getClient('KsuWellsClient')
  const wellContainers = await client.ksuWellsGetCompanyWellContainers(companyKsuUid)
  return { fields: wellContainers.fields, licenseAreas: wellContainers.licenseAreas }
}

export const searchKsuWellsGroupsApi = async (
  searchString: string,
  companyKsuId?: Nullable<KsuCompanyId>,
  licenseAreaKsuId?: Nullable<KsuLicenseAreaId>,
  fieldKsuId?: Nullable<KsuFieldId>,
): Promise<KsuWellsSearchGroup[]> => {
  const client = getClient('KsuWellsClient')
  const apiGroups = await client.ksuWellsSearchWellGroups({
    where: {
      searchString,
      companyKsuId,
      licenseAreaKsuId,
      fieldKsuId,
    },
    groupBy: 'Field',
  })

  return apiGroups.map(group => ({
    by: group.by,
    id: group.id,
    name: group.name,
    wellsCount: group.count ?? 0,
    wells: [],
  }))
}

export const searchKsuWellsApi = async (
  projectId: ProjectId,
  group: KsuWellsSearchGroup,
  searchString: string,
  companyKsuId?: Nullable<KsuCompanyId>,
  licenseAreaKsuId?: Nullable<KsuLicenseAreaId>,
  fieldKsuId?: Nullable<KsuFieldId>,
  takeRowsCount?: number,
  skipRowsCount?: number,
): Promise<KsuWell[]> => {
  const client = getClient('KsuWellsClient')
  const apiWells = await client.ksuWellsSearchWells({
    where: {
      searchString,
      companyKsuId,
      licenseAreaKsuId,
      fieldKsuId,
    },
    forProject: projectId,
    inGroup: {
      by: group.by,
      id: group.id,
      name: group.name,
    },
    takeRowsCount,
    skipRowsCount,
  })

  return apiWells.map(well => ({
    ksuUid: well.ksuUid,
    wellUid: well.wellUid,
    wellName: well.wellName,
    changedAt: well.changedAt,
    state: well.state === 'Ready' ? 'addedToProject' : 'notstarted',
  }))
}

/// grids

export const getAllGridHeadersByProjectIdApi = (projectId: ProjectId): Promise<MapInfo[]> => {
  const client = getClient('ProjectGridClient')
  return client
    .projectGridGetAllGridHeaders(projectId)
    .then(headers => headers.map(convertToMapInfo))
}

export const calculateSurfaceBySequentialConvergenceApi = (
  sequentialConvergence: SequentialConvergenceParams,
  buildInfo: BuildInfo,
): Promise<string> => {
  const client = getClient('GridBuilderClient')
  return client.gridBuilderRunSc({ ...sequentialConvergence, buildInfo })
}

export const calculateSurfaceByKrigingApi = (
  kriging: KrigingParams,
  buildInfo: BuildInfo,
): Promise<string> => {
  const client = getClient('GridBuilderClient')
  return client.gridBuilderRunKriging({ ...kriging, buildInfo })
}

export const calculateSurfaceByRBFApi = (
  kernel: RbfKernel,
  buildInfo: BuildInfo,
): Promise<string> => {
  const client = getClient('GridBuilderClient')
  return client.gridBuilderRunRbf({ kernel, buildInfo })
}

export const calculateSurfaceByMimimalCurvatureApi = (
  params: MinimalCurvatureParams,
  buildInfo: BuildInfo,
): Promise<string> => {
  const client = getClient('GridBuilderClient')

  return client.gridBuilderRunMinCurvature({
    maxIter: params.iterationsCount,
    maxDeviation: params.maxDiscrepancy,
    buildInfo,
  })
}

export const calculateSurfaceByMovingAverageApi = (
  params: MovingAverageParams,
  buildInfo: BuildInfo,
): Promise<string> => {
  const client = getClient('GridBuilderClient')
  return client.gridBuilderRunMovingAverage({
    rx: params.ellipseBigRadius,
    ry: params.ellipseSmallRadius,
    angle: params.anisotropyAngle ?? 0,
    minCount: params.neighborsCount,
    buildInfo,
  })
}

export const calculateGridFittingApi = (
  projectId: ProjectId,
  mapId: MapId,
  newMapName: string,
  markerName: string,
): Promise<MapFetchingResult> => {
  const client = getClient('GridFittingClient')
  return client
    .gridFittingRunFitting(convertToGridFittingParams(projectId, mapId, markerName, newMapName))
    .then(result => convertToMapFittingResult(result, projectId))
}

/// velocityModeling
export const upsertVelocityModelApi = (
  projectId: ProjectId,
  model: CreateVelocityModel,
  modelId?: string,
): Promise<ResultCreateVelocityModel> => {
  const client = getClient('ProjectVelocityModelClient')
  const body = {
    name: model.name,
    borderVelocityTop: model.borderVelocityTop,
    layers: model.layers,
    borderVelocityBot: model.borderVelocityBot,
  }
  const result = modelId
    ? client.projectVelocityModelUpdateModel(projectId, modelId, body)
    : client.projectVelocityModelAddModel(projectId, body)
  return result.then(info => convertToResultCreateVelocityModel(projectId, info))
}

export const applyVelocityDomainsApi = (
  projectId: ProjectId,
  { domain, velocityModelId, objects }: DomainConvertInfo,
): Promise<Omit<DomainConvertObject, 'type'>[]> => {
  const client = getClient('VelocityModelingClient')
  const domainObjects = objects.map(({ id, type }) => ({ id, type: convertToApiElementType(type) }))
  return client
    .velocityModelingAssociateToDomain(projectId, {
      domain,
      velocityModelId,
      objects: domainObjects,
    })
    .then(info => info.elements.map(convertToElementConvertInfo))
}

export const getProjectVelocityModelsApi = (projectId: ProjectId): Promise<VelocityModel[]> => {
  const client = getClient('ProjectVelocityModelClient')
  return client
    .projectVelocityModelGetProjectVelocityModels(projectId)
    .then(headers => headers.map(convertToVelocityModelHeader))
}

export const convertMarkersToPointsApi = (
  markerName: string,
  projectId: ProjectId,
): Promise<string> => {
  const projectClient = getClient('PointsetBuilderClient')
  return projectClient.pointsetBuilderCreatePointsetByMarkerName(projectId, {
    pointsetName: markerName,
    markerName,
  })
}

/// licenseAreas

export const getLicenseAreaIdForFieldApi = async (
  fieldId: FieldId,
): Promise<LicenseAreaId | undefined> => {
  const client = getClient('LicenseAreasClient')
  const [licenseAreaId] = await client.licenseAreasGetLicenseAreaIdsForField(fieldId)
  return licenseAreaId
}
